// src/oura/merchant/drawers/DocAnswer.tsx
//
// Answering a document's questions in Oura (B19, scope 2.9 client side),
// inside the document's sheet (DocSheet, Documents.tsx). One question at a
// time, the way QuestionSheet answers one we asked: the question whole, its
// answer box by kind, and Send answer pinned in the foot.
//
//   agree    Agree, or Change with a note ("What should change?")
//   change   what should change, in their words
//   choice   one of the question's options
//   text     a written answer, with a file if they like (also a question
//            Oura holds no kind for)
//   file     a file, stored in their Drive folder ("Files from <client>")
//
// Oura posts the answer into the doc's thread on their behalf. Agree
// resolves it (the approval of record); anything else leaves it with us.
// After a send the next question in the doc shows; once none is left the
// doc leaves the queue on the next read, and the host offers the next item.
//
// It calls the documents write layer only (answerQuestion, uploadClientFile),
// never a pack route: a doc answer must never become a pack item. Shown only
// where DOCS_WRITE_ON; elsewhere the sheet keeps Phase 1's "Reply in the
// document". `AnsweredFold` lists what they answered and when (route 12's
// `answered`), on any doc that carries it.

import { useEffect, useId, useRef, useState, type ReactNode, type Ref } from "react";
import { CheckIcon, FileIcon, PaperclipIcon, SendIcon, UploadIcon } from "lucide-react";
import {
    DOCS_WRITE_ON,
    answerQuestion,
    uploadClientFile,
    type ClientAnswerIn,
    type MerchantAnswered,
    type QuestionKind,
} from "@service/aeo/clientDocsWrite";
import type { DocsFailure, MerchantDocument } from "@service/aeo/clientDocs";
import { useViewAs } from "@hook/use-view-as";
import { useWritesPaused } from "@context/maintenance";
import { Dot, DrawerBlock, DrawerFold, DrawerFoot, FOCUS_RING, LATE_TEXT, PillButton, SURFACE, TYPE, useDrawerFocusClaim } from "@oura/ui";
import { docDate, trimText } from "@oura/docs";
import { cn } from "../../lib/cn";
import { Options } from "../AnswerCard";
import { publicFacingLine, type AnswerDraft } from "../asks";
import { questionText } from "../docAsks";
import type { SheetFlow } from "./sheetFlow";

const FIELD = cn(
    TYPE.tb,
    "block w-full resize-y rounded-xl border-0 bg-gray-50 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600",
);

/** What a file answer may be: a PDF or a PNG, JPEG or WebP photo, up to 20 MB. The server takes nothing else (upload_type_not_allowed), so the picker offers nothing else. */
const FILE_ACCEPT = "image/png,image/jpeg,image/webp,.pdf,.png,.jpg,.jpeg,.webp";
const FILE_MAX_BYTES = 20 * 1024 * 1024;

/** A callback ref that may hand back its cleanup (React 19), as useDrawerFocusClaim's does. */
type ClaimRef = (el: HTMLElement | null) => void | (() => void);

type OpenQuestion = MerchantDocument["openQuestions"][number];

/** The host's draft key for a doc question: doc and thread ids, apart from pack question ids. */
const docAnswerKey = (docId: string, threadId: string): string => `doc:${docId}:${threadId}`;

/** The answer box a question gets. A choice with fewer than 2 options, or no kind on record, is a written answer. */
function kindOf(q: OpenQuestion): Exclude<QuestionKind, "choice"> | "choice" {
    if (q.kind === "choice") return (q.options ?? []).length >= 2 ? "choice" : "text";
    // "Please confirm the address": Agree or Change, like every other confirm, never a bare box.
    if ((q.kind ?? "text") === "text" && /^\s*(please\s+)?confirm\b/i.test(questionText(q.text))) return "agree";
    return q.kind ?? "text";
}

/** What a draft sends, or null while it is not an answer yet. */
function docAnswerOf(kind: QuestionKind, draft: AnswerDraft | undefined): ClientAnswerIn | null {
    if (!draft) return null;
    const text = draft.text?.trim() ?? "";
    switch (kind) {
        case "agree":
            if (draft.pick === "agree") return { kind: "agree" };
            return draft.pick === "change" && text ? { kind: "change", note: text } : null;
        case "change":
            return text ? { kind: "change", note: text } : null;
        case "choice":
            return draft.option ? { kind: "option", option: draft.option } : null;
        case "file": {
            const files = draft.files?.length ? draft.files : draft.file ? [draft.file] : [];
            return files.length ? { kind: "file", fileKey: files[0].fileKey, fileKeys: files.map((f) => f.fileKey) } : null;
        }
        default:
            // A tap on Yes or No, for a plain yes/no question asked as text.
            if (draft.option && draft.option !== SOMETHING_ELSE && !text) return { kind: "text", text: draft.option };
            // Words, a file, or both: a price list typed out, or its photo.
            if (text) return draft.file ? { kind: "text", text, fileKey: draft.file.fileKey } : { kind: "text", text };
            return draft.file ? { kind: "file", fileKey: draft.file.fileKey } : null;
    }
}

/** The tap that opens a box for any other answer, beside Yes and No. */
const SOMETHING_ELSE = "Something else";

/** A plain yes/no question asked as text ("Is the Mont Kiara phone the same?"): it gets Yes and No to tap, and a box for anything else. */
function isYesNoQuestion(text: string): boolean {
    const t = questionText(text).trim();
    return /^(is|are|do|does|did|can|could|should|shall|will|would|has|have|may)\b/i.test(t) && /\?\s*$/.test(t) && !/\bor\b/i.test(t);
}

/** What Send says when the answer is not there yet, in their words. */
function emptyAnswerText(kind: QuestionKind, draft: AnswerDraft | undefined): string {
    switch (kind) {
        case "agree":
            return draft?.pick === "change" ? "Say what should change first." : "Agree, or say what should change.";
        case "change":
            return "Say what should change first.";
        case "choice":
            return "Pick one of the options.";
        case "file":
            return "Add the file first.";
        default:
            return "Write your answer first.";
    }
}

/** A refused or failed send, in their words. */
function sendFailText(f: DocsFailure): string {
    if (f.reason === "notServed") return "Answering here isn't open yet. Reply in the document.";
    if (f.reason === "notFound") return "That question is no longer in the document.";
    if (f.reason === "network") return "Your answer did not send. It is still here. Try again.";
    switch (f.code) {
        case "thread_resolved":
            return "This question is already answered.";
        case "note_required":
            return "Say what should change first.";
        case "option_unknown":
            return "Pick one of the options.";
        case "answer_invalid":
            return f.message || "That answer is missing something.";
        default:
            return "Your answer did not send. It is still here. Try again.";
    }
}

export interface DocAnswerProps {
    doc: MerchantDocument;
    /** The account manager's name: who gets the answer. */
    who: string;
    flow: SheetFlow;
    drafts: Record<string, AnswerDraft>;
    setDraft: (key: string, draft: AnswerDraft | null) => void;
    /** "Open the document", as the sheet draws it: primary or secondary. */
    openLink: (primary: boolean) => ReactNode;
}

/**
 * The answer body and its foot. Renders nothing of its own without the
 * write switch; the sheet does not mount it then either.
 */
export function DocAnswer({ doc, who, flow, drafts, setDraft, openLink }: DocAnswerProps) {
    const claim = useDrawerFocusClaim();
    const errorId = useId();
    const boxRef = useRef<HTMLDivElement>(null);
    /** Sent from here and not yet gone from the doc's list on a read: never offered twice. */
    const [sent, setSent] = useState<ReadonlySet<string>>(() => new Set());
    const [currentId, setCurrentId] = useState<string | null>(null);
    const [sending, setSending] = useState(false);
    // Staff viewing as the merchant read; they do not answer (#4112).
    const { active: readOnly } = useViewAs();
    const writesPaused = useWritesPaused();
    const [error, setError] = useState<string | null>(null);

    // The drafts as they stand now: a file that finishes uploading joins the words
    // typed while it uploaded, never the draft from the moment it was picked.
    const draftsRef = useRef(drafts);
    useEffect(() => {
        draftsRef.current = drafts;
    }, [drafts]);

    const { setBusy } = flow;
    useEffect(() => {
        setBusy(sending);
    }, [sending, setBusy]);
    useEffect(() => () => setBusy(false), [setBusy]);

    const waiting = doc.openQuestions.filter((q) => questionText(q.text) && !sent.has(q.id));
    // "Question x of y" counts against the doc's questions as they were on opening: one sent keeps its place.
    const [startIds] = useState<string[]>(() => doc.openQuestions.filter((q) => questionText(q.text)).map((q) => q.id));
    const index = Math.max(0, waiting.findIndex((q) => q.id === currentId));
    const q = waiting[index] ?? null;
    const key = q ? docAnswerKey(doc.id, q.id) : null;
    const draft = key ? drafts[key] : undefined;
    const kind = q ? kindOf(q) : "text";
    const answer = q ? docAnswerOf(kind, draft) : null;

    // A new question on show: its first control takes focus, as a question sheet's does on open.
    const shownId = q?.id ?? null;
    const lastShown = useRef(shownId);
    useEffect(() => {
        if (lastShown.current === shownId) return;
        lastShown.current = shownId;
        boxRef.current?.querySelector<HTMLElement>("fieldset button, fieldset textarea, fieldset input")?.focus();
    }, [shownId]);

    if (!DOCS_WRITE_ON) return null;

    const setThis = (patch: AnswerDraft | null) => {
        if (!key) return;
        setDraft(key, patch);
        if (error) setError(null);
    };
    /** The file on this question's latest draft, added or taken off. */
    const fileFor = (forKey: string | null) => (file: AnswerDraft["file"] | null) => {
        if (!forKey) return;
        setDraft(forKey, { ...draftsRef.current[forKey], file: file ?? undefined });
        setError(null);
    };

    /** Every file on this question's latest draft: added one by one, or taken off. */
    const filesFor = (forKey: string | null) => (update: (was: NonNullable<AnswerDraft["files"]>) => NonNullable<AnswerDraft["files"]>) => {
        if (!forKey) return;
        const was = draftsRef.current[forKey];
        const list = update(was?.files ?? (was?.file ? [was.file] : []));
        setDraft(forKey, { ...was, file: undefined, files: list });
        setError(null);
    };

    const send = async (override?: ClientAnswerIn) => {
        if (!q || sending || writesPaused) return;
        const answer_ = override ?? answer;
        if (!answer_) {
            setError(emptyAnswerText(kind, draft));
            return;
        }
        setSending(true);
        setError(null);
        // Busy always clears, even when something throws after the request: a
        // latched send would leave the box and the drawer's arrows stuck until
        // the sheet is closed.
        try {
            const r = await answerQuestion(doc.id, q.id, answer_);
            if (!r.ok) {
                // Answered already (in the doc, or on another device): it leaves the list
                // here too, and the words go in the drawer's line about this question. As
                // an error they would sit under the next question and read as being about it.
                if (r.code === "thread_resolved") {
                    setDraft(docAnswerKey(doc.id, q.id), null);
                    setSent((s) => new Set(s).add(q.id));
                    setCurrentId(null);
                    flow.said("This question was already answered.");
                    return;
                }
                setError(sendFailText(r));
                return;
            }
            setDraft(docAnswerKey(doc.id, q.id), null);
            setSent((s) => new Set(s).add(q.id));
            setCurrentId(null);
            flow.said(answer_.kind === "agree" ? "Agreed. It's in the document." : `Sent to ${who}. It's in the document.`);
        } catch {
            setError("Your answer did not send. It is still here. Try again.");
        } finally {
            setSending(false);
        }
    };

    const skip = () => {
        const next = waiting[(index + 1) % waiting.length];
        if (next) setCurrentId(next.id);
        setError(null);
        // The last "Sent" was about the question before; it does not follow them to this one.
        flow.clear?.();
    };
    const previous = () => {
        const prev = waiting[(index - 1 + waiting.length) % waiting.length];
        if (prev) setCurrentId(prev.id);
        setError(null);
        flow.clear?.();
    };

    const message = flow.message ? { tone: "success" as const, text: flow.message } : null;

    if (!q) {
        // Every question sent from here: the doc leaves the queue on the next read.
        return (
            <>
                <DrawerBlock>
                    <p className="m-0">Your answers are in the document. {who} replies next.</p>
                </DrawerBlock>
                <DrawerFoot done={flow.done} message={message} first={openLink(false)} />
            </>
        );
    }

    const at = startIds.indexOf(q.id);
    const label = startIds.length > 1 && at >= 0 ? `Question ${at + 1} of ${startIds.length}` : waiting.length > 1 ? `Question ${index + 1} of ${waiting.length}` : "Your answer";
    return (
        <>
            <DrawerBlock label={label} busy={sending}>
                <div ref={boxRef}>
                    <p className="m-0 whitespace-pre-line break-words font-medium text-gray-900">{questionText(q.text)}</p>
                    <fieldset disabled={sending || readOnly} className="m-0 min-w-0 border-0 p-0" aria-describedby={error ? errorId : undefined}>
                        <legend className="sr-only">Your answer to: {questionText(q.text)}</legend>
                        {kind === "agree" && (
                            <AgreeOrChange
                                draft={draft}
                                set={(p) => setThis({ ...draft, ...p })}
                                claim={claim}
                                onSubmit={() => void send()}
                                // Agree is the answer: it sends straight away. Change opens the box.
                                onAgree={() => {
                                    setThis({ ...draft, pick: "agree" });
                                    void send({ kind: "agree" });
                                }}
                            />
                        )}
                        {kind === "choice" && (
                            <Options label={questionText(q.text)} options={q.options ?? []} value={draft?.option} onPick={(option) => setThis({ ...draft, option })} claim={claim} />
                        )}
                        {kind === "file" && (
                            <DocFilesPick
                                files={draft?.files ?? (draft?.file ? [draft.file] : [])}
                                asked={askedCount(q.text)}
                                noun={askedNoun(q.text)}
                                onFiles={filesFor(key)}
                                claim={claim}
                            />
                        )}
                        {kind === "text" && isYesNoQuestion(q.text) && (
                            <Options
                                label={questionText(q.text)}
                                options={["Yes", "No", SOMETHING_ELSE]}
                                value={draft?.option}
                                onPick={(option) => setThis({ ...draft, option, text: option === SOMETHING_ELSE ? draft?.text : "" })}
                                claim={claim}
                            />
                        )}
                        {(kind === "change" || (kind === "text" && (!isYesNoQuestion(q.text) || draft?.option === SOMETHING_ELSE))) && (
                            <textarea
                                ref={kind === "text" && isYesNoQuestion(q.text) ? undefined : (claim as Ref<HTMLTextAreaElement>)}
                                rows={3}
                                value={draft?.text ?? ""}
                                onChange={(e) => setThis({ ...draft, text: e.target.value })}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                                        e.preventDefault();
                                        void send();
                                    }
                                }}
                                placeholder={kind === "change" ? "What should change?" : "Type your answer"}
                                aria-label={questionText(q.text)}
                                className={cn(FIELD, "mt-3")}
                            />
                        )}
                        {kind === "text" && (
                            <DocFilePick optional file={draft?.file} onFile={fileFor(key)} />
                        )}
                    </fieldset>
                    {error && (
                        <p id={errorId} role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-2")}>
                            {error}
                        </p>
                    )}
                    <p className={cn(TYPE.tm, "m-0 mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-500")}>
                        <span>It goes into the document as your reply.{publicFacingLine(q.text, who) ? ` ${publicFacingLine(q.text, who)}` : ""}</span>
                        {waiting.length > 1 && (
                            <>
                                <PillButton variant="text" disabled={sending} onClick={previous}>
                                    Previous
                                </PillButton>
                                <PillButton variant="text" disabled={sending} onClick={skip}>
                                    Skip for now
                                </PillButton>
                            </>
                        )}
                    </p>
                </div>
            </DrawerBlock>
            <DrawerFoot
                done={flow.done}
                message={message}
                first={
                    <PillButton writes variant="primary" icon={SendIcon} busy={sending} disabled={readOnly} onClick={() => void send()}>
                        Send answer
                    </PillButton>
                }
                second={openLink(false)}
            />
        </>
    );
}

/** Agree, or Change with what should change. */
function AgreeOrChange({
    draft,
    set,
    claim,
    onSubmit,
    onAgree,
}: {
    draft: AnswerDraft | undefined;
    set: (patch: AnswerDraft) => void;
    claim: ClaimRef;
    onSubmit: () => void;
    onAgree: () => void;
}) {
    const pick = draft?.pick;
    const changeBox = useRef<HTMLTextAreaElement>(null);
    return (
        <>
            <div
                className="mt-3 flex flex-wrap gap-2"
                // Agree is the pill that takes focus: the kit's pill takes no ref of its own.
                ref={(el) => {
                    const agree = el?.querySelector("button");
                    return agree ? claim(agree) : undefined;
                }}
            >
                <PillButton writes variant={pick === "agree" ? "primary" : "secondary"} icon={CheckIcon} aria-pressed={pick === "agree"} onClick={onAgree}>
                    Agree
                </PillButton>
                <PillButton
                    variant={pick === "change" ? "primary" : "secondary"}
                    aria-pressed={pick === "change"}
                    onClick={() => {
                        set({ pick: "change" });
                        requestAnimationFrame(() => changeBox.current?.focus());
                    }}
                >
                    Change
                </PillButton>
            </div>
            {pick === "change" && (
                <textarea
                    ref={changeBox}
                    rows={3}
                    value={draft?.text ?? ""}
                    onChange={(e) => set({ text: e.target.value })}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            onSubmit();
                        }
                    }}
                    placeholder="What should change?"
                    aria-label="What should change?"
                    className={cn(FIELD, "mt-3")}
                />
            )}
        </>
    );
}

const NUMBER_WORDS: Record<string, number> = { two: 2, three: 3, four: 4, five: 5, six: 6 };
const ASKED_RE = /\b(\d+|two|three|four|five|six)\s+(photos?|pictures?|images?|files?|menus?|documents?)\b/i;

/** How many files a question asks for ("Send 3 photos" asks for 3); 1 when it names no number. */
function askedCount(text: string): number {
    const m = ASKED_RE.exec(text);
    if (!m) return 1;
    const n = Number(m[1]) || NUMBER_WORDS[m[1].toLowerCase()] || 1;
    return Math.min(Math.max(n, 1), 10);
}

/** What it asks for, plural: "photos", else "files". */
function askedNoun(text: string): string {
    const m = ASKED_RE.exec(text);
    const word = m ? m[2].toLowerCase().replace(/s$/, "") : "file";
    return `${word}s`;
}

/**
 * A file answer that may take several: each uploads to their Drive folder
 * the moment it is chosen and joins the list, with a count against what the
 * question asked for ("1 of 3 photos"). Choosing again adds, never replaces.
 */
function DocFilesPick({
    files,
    asked,
    noun,
    onFiles,
    claim,
}: {
    files: NonNullable<AnswerDraft["files"]>;
    asked: number;
    noun: string;
    onFiles: (update: (was: NonNullable<AnswerDraft["files"]>) => NonNullable<AnswerDraft["files"]>) => void;
    claim?: ClaimRef;
}) {
    const inputId = useId();
    const [uploading, setUploading] = useState(0);
    const [problem, setProblem] = useState<string | null>(null);
    const [keptIn, setKeptIn] = useState<string | null>(null);

    // Staff viewing as the merchant read; choosing uploads at once, so it steps aside (#4112).
    const { active: readOnly } = useViewAs();

    const writesPaused = useWritesPaused();

    const chooseAll = async (picked: File[]) => {
        if (readOnly || writesPaused) return;
        const big = picked.filter((f) => f.size > FILE_MAX_BYTES);
        const ok = picked.filter((f) => f.size <= FILE_MAX_BYTES);
        setProblem(big.length ? `${big.map((f) => f.name).join(", ")} ${big.length === 1 ? "is" : "are"} over 20 MB.` : null);
        setUploading((n) => n + ok.length);
        for (const f of ok) {
            const r = await uploadClientFile(f);
            setUploading((n) => n - 1);
            if (!r.ok) {
                // The server's own reason first ("That file is empty.", "Only PDF, PNG, JPEG or WebP files can be uploaded."): a retry does not fix those.
                setProblem(r.reason === "notServed" ? "Sending files here isn't open yet." : r.message ? `${f.name}: ${r.message}` : `${f.name} did not upload. Try again.`);
                continue;
            }
            setKeptIn(r.data.folderPath || null);
            onFiles((was) => [...was, { fileKey: r.data.fileKey, name: r.data.name }]);
        }
    };

    const count = asked > 1 ? `${files.length} of ${asked} ${noun}` : null;
    return (
        <div className="mt-3">
            <input
                ref={claim ? (claim as Ref<HTMLInputElement>) : undefined}
                id={inputId}
                type="file"
                multiple
                accept={FILE_ACCEPT}
                disabled={uploading > 0 || readOnly || writesPaused}
                className="peer sr-only"
                onChange={(e) => {
                    const picked = Array.from(e.target.files ?? []);
                    e.target.value = "";
                    if (picked.length) void chooseAll(picked);
                }}
            />
            <label
                htmlFor={inputId}
                className={cn(
                    TYPE.tc,
                    "relative inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full bg-white px-4 font-medium text-gray-900 ring-1 ring-inset ring-black/[0.08] transition-colors after:absolute after:-inset-y-1.5 after:inset-x-0 after:content-[''] hover:bg-gray-50 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-blue-600",
                    uploading > 0 && "cursor-wait opacity-60",
                    readOnly && "pointer-events-none cursor-default opacity-50",
                )}
            >
                <UploadIcon aria-hidden size={14} />
                {uploading > 0 ? "Uploading…" : files.length === 0 ? (asked > 1 ? `Choose ${noun}` : "Choose a file") : asked > 1 ? `Add more ${noun}` : "Add another file"}
            </label>
            {count && <span className={cn(TYPE.tm, "ml-2 tabular-nums", files.length >= asked ? "text-emerald-700" : "text-gray-500")}>{count}</span>}
            {files.length > 0 && (
                <ul className="m-0 mt-2 list-plain space-y-1 p-0">
                    {files.map((f) => (
                        <li key={f.fileKey} className={cn(TYPE.tc, "flex items-center gap-2 text-gray-900")}>
                            <FileIcon aria-hidden size={14} className="shrink-0 text-emerald-700" />
                            <span className="min-w-0 flex-1 truncate">{f.name}</span>
                            <PillButton variant="text" onClick={() => onFiles((was) => was.filter((x) => x.fileKey !== f.fileKey))} aria-label={`Remove ${f.name}`}>
                                Remove
                            </PillButton>
                        </li>
                    ))}
                </ul>
            )}
            {files.length > 0 && uploading === 0 && keptIn && <p className={cn(TYPE.tm, "m-0 mt-1.5 text-gray-500")}>Kept in your Drive folder, {keptIn}.</p>}
            {problem && (
                <p role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-1.5")}>
                    {problem}
                </p>
            )}
        </div>
    );
}

/** A file answer: it uploads to their Drive folder the moment it is chosen, and the draft keeps its key. */
function DocFilePick({
    file,
    onFile,
    claim,
    optional = false,
}: {
    file: AnswerDraft["file"];
    onFile: (file: AnswerDraft["file"] | null) => void;
    claim?: ClaimRef;
    /** Beside a written answer: the file goes with the words, and can be taken off again. */
    optional?: boolean;
}) {
    const inputId = useId();
    const [uploading, setUploading] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);
    /** Where the upload was kept: their Drive folder, "Files from <client>". */
    const [keptIn, setKeptIn] = useState<string | null>(null);

    // Staff viewing as the merchant read; choosing uploads at once, so it steps aside (#4112).
    const { active: readOnly } = useViewAs();

    const writesPaused = useWritesPaused();

    const choose = async (picked: File) => {
        if (readOnly || writesPaused) return;
        if (picked.size > FILE_MAX_BYTES) {
            setProblem("That file is over 20 MB.");
            return;
        }
        setProblem(null);
        setUploading(true);
        const r = await uploadClientFile(picked);
        setUploading(false);
        if (!r.ok) {
            setProblem(r.reason === "notServed" ? "Sending files here isn't open yet." : r.message ?? "That file did not upload. Try again.");
            return;
        }
        setKeptIn(r.data.folderPath || null);
        onFile({ fileKey: r.data.fileKey, name: r.data.name });
    };

    return (
        <div className="mt-3">
            <input
                ref={claim ? (claim as Ref<HTMLInputElement>) : undefined}
                id={inputId}
                type="file"
                accept={FILE_ACCEPT}
                disabled={uploading || readOnly || writesPaused}
                className="peer sr-only"
                onChange={(e) => {
                    const picked = e.target.files?.[0];
                    e.target.value = "";
                    if (picked) void choose(picked);
                }}
            />
            <label
                htmlFor={inputId}
                className={cn(
                    TYPE.tc,
                    "relative inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full px-4 font-medium ring-1 ring-inset transition-colors after:absolute after:-inset-y-1.5 after:inset-x-0 after:content-[''] peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-blue-600",
                    file ? "bg-emerald-50 text-emerald-800 ring-emerald-200" : "bg-white text-gray-900 ring-black/[0.08] hover:bg-gray-50",
                    uploading && "cursor-wait opacity-60",
                    readOnly && "pointer-events-none cursor-default opacity-50",
                )}
            >
                {file ? <FileIcon aria-hidden size={14} /> : <UploadIcon aria-hidden size={14} />}
                <span className="max-w-[16rem] truncate">{uploading ? "Uploading…" : file ? file.name : optional ? "Add a file" : "Choose a file"}</span>
            </label>
            {file && !uploading && !optional && <span className={cn(TYPE.tm, "ml-2 text-gray-500")}>Ready to send. Choose again to replace it.</span>}
            {file && !uploading && keptIn && <p className={cn(TYPE.tm, "m-0 mt-1.5 text-gray-500")}>Kept in your Drive folder, {keptIn}.</p>}
            {file && !uploading && optional && (
                <PillButton variant="text" className="ml-2" onClick={() => onFile(null)} aria-label={`Remove ${file.name}`}>
                    Remove
                </PillButton>
            )}
            {problem && (
                <p role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-1.5")}>
                    {problem}
                </p>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------------- *
 * What they answered
 * ------------------------------------------------------------------------- */

const VIA_WORDS: Record<MerchantAnswered["via"], string | null> = {
    oura: null,
    doc: "in the document",
    email: "by email",
    whatsapp: "on WhatsApp",
    call: "on a call",
};

/** Their answer in a few words: "Agreed", or their words. A file shows as its own link. */
function answeredWhat(a: MerchantAnswered): string {
    if (a.answer === "agree") return "Agreed";
    const words = a.words.trim();
    if (words) return trimText(words, 140);
    return a.state === "approved" ? "Resolved" : "Answered";
}

/**
 * "You answered · 3": each question they answered, numbered as in the doc,
 * with what they said, when, and where when it was not here. Folded, with
 * the latest date on its head. Nothing when the server sends none.
 */
export function AnsweredFold({ answered, who }: { answered: MerchantAnswered[] | undefined; who: string }) {
    if (!answered || answered.length === 0) return null;
    const latest = docDate(answered[0].at);
    return (
        <DrawerFold id="merchant-doc.answered" label={`You answered · ${answered.length}`} note={latest ? `Last ${latest}` : undefined}>
            <ol className="list-plain m-0 p-0">
                {answered.map((a) => {
                    const when = docDate(a.at);
                    const via = VIA_WORDS[a.via];
                    return (
                        <li key={a.id} className={cn("flex gap-3 border-t py-2.5 first:border-t-0 first:pt-0 last:pb-0", SURFACE.hairline)}>
                            <span aria-hidden className={cn(TYPE.tc, "w-4 shrink-0 pt-px tabular-nums text-gray-400")}>
                                {a.number ?? "·"}
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="m-0 break-words text-gray-700">{questionText(a.text)}</p>
                                {!(a.file && a.answer === "file") && (
                                    <p className="m-0 mt-0.5 whitespace-pre-line break-words font-medium text-gray-900">{answeredWhat(a)}</p>
                                )}
                                {a.file && (
                                    <p className="m-0 mt-0.5">
                                        <a
                                            href={a.file.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={cn("inline-flex items-center gap-1.5 rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}
                                        >
                                            <PaperclipIcon aria-hidden size={14} />
                                            {a.file.name}
                                        </a>
                                    </p>
                                )}
                                {/* Dot and word, once: emerald for approved, blue while it is with us. */}
                                <p className={cn(TYPE.tm, "m-0 mt-0.5 flex items-center gap-1.5 tabular-nums text-gray-500")}>
                                    <Dot tone={a.state === "approved" ? "green" : "blue"} />
                                    <span className="min-w-0">
                                        <span className={a.state === "approved" ? "text-emerald-700" : "text-blue-700"}>{a.state === "approved" ? "Approved" : `With ${who}`}</span>
                                        {[when, via].filter(Boolean).map((x) => ` · ${x}`).join("")}
                                    </span>
                                </p>
                            </div>
                        </li>
                    );
                })}
            </ol>
        </DrawerFold>
    );
}
